import React, { useState, useRef, useEffect, useCallback } from "react";
import { Modal } from "react-bootstrap";
import CloseButton from "react-bootstrap/CloseButton";
import "./style.scss";
import CommonButton from "../../commonButton";

const ESignatureModal = ({
  show,
  onHide,
  field = null,
  updateField,
  onCloseParentModal,
  signerType = null,
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [useSavedSignature, setUseSavedSignature] = useState(false);
  const [showDrawPad, setShowDrawPad] = useState(false); // Toggle between views
  const [fullName, setFullName] = useState("");
  const [saveConsent, setSaveConsent] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [signatureLoaded, setSignatureLoaded] = useState(false);

  useEffect(() => {
    if (show) {
      // Small delay to ensure canvas is fully rendered
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
          setSignatureLoaded(true);
          return;
        }

        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setSignatureLoaded(false);

        // Load any existing signature
        if (field && field.value) {
          try {
            const parsed =
              typeof field.value === "string"
                ? JSON.parse(field.value)
                : field.value;

            if (
              parsed.signature &&
              parsed.signature.startsWith("data:image/")
            ) {
              const img = new Image();
              img.onload = () => {
                // Ensure canvas is still available and has correct dimensions
                if (
                  canvasRef.current &&
                  canvas.width > 0 &&
                  canvas.height > 0
                ) {
                  // Calculate aspect ratio to maintain signature proportions
                  const imgAspectRatio = img.width / img.height;
                  const canvasAspectRatio = canvas.width / canvas.height;

                  let drawWidth, drawHeight, drawX, drawY;

                  if (imgAspectRatio > canvasAspectRatio) {
                    // Image is wider than canvas aspect ratio - fit to width
                    drawWidth = canvas.width * 0.8; // Use 80% of canvas width
                    drawHeight = drawWidth / imgAspectRatio;
                    drawX = (canvas.width - drawWidth) / 2;
                    drawY = (canvas.height - drawHeight) / 2;
                  } else {
                    // Image is taller than canvas aspect ratio - fit to height
                    drawHeight = canvas.height * 0.8; // Use 80% of canvas height
                    drawWidth = drawHeight * imgAspectRatio;
                    drawX = (canvas.width - drawWidth) / 2;
                    drawY = (canvas.height - drawHeight) / 2;
                  }

                  ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
                }
                setSignatureLoaded(true);
              };
              img.onerror = () => {
                setSignatureLoaded(true);
              };
              img.src = parsed.signature;
            } else {
              setSignatureLoaded(true);
            }
          } catch (e) {
            // Handle case where field.value is just a data URL
            if (field.value && field.value.startsWith("data:image/")) {
              const img = new Image();
              img.onload = () => {
                if (
                  canvasRef.current &&
                  canvas.width > 0 &&
                  canvas.height > 0
                ) {
                  // Calculate aspect ratio to maintain signature proportions
                  const imgAspectRatio = img.width / img.height;
                  const canvasAspectRatio = canvas.width / canvas.height;

                  let drawWidth, drawHeight, drawX, drawY;

                  if (imgAspectRatio > canvasAspectRatio) {
                    // Image is wider than canvas aspect ratio - fit to width
                    drawWidth = canvas.width * 0.8; // Use 80% of canvas width
                    drawHeight = drawWidth / imgAspectRatio;
                    drawX = (canvas.width - drawWidth) / 2;
                    drawY = (canvas.height - drawHeight) / 2;
                  } else {
                    // Image is taller than canvas aspect ratio - fit to height
                    drawHeight = canvas.height * 0.8; // Use 80% of canvas height
                    drawWidth = drawHeight * imgAspectRatio;
                    drawX = (canvas.width - drawWidth) / 2;
                    drawY = (canvas.height - drawHeight) / 2;
                  }

                  ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
                }
                setSignatureLoaded(true);
              };
              img.onerror = () => {
                setSignatureLoaded(true);
              };
              img.src = field.value;
            } else {
              setSignatureLoaded(true);
            }
          }
        } else {
          setSignatureLoaded(true);
        }
      }, 50); // 50ms delay to ensure canvas is ready
    }
  }, [show, field]);

  const loadSignatureOnCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Load any existing signature
    if (field && field.value) {
      try {
        const parsed =
          typeof field.value === "string"
            ? JSON.parse(field.value)
            : field.value;

        if (parsed.signature && parsed.signature.startsWith("data:image/")) {
          const img = new Image();
          img.onload = () => {
            // Ensure canvas is still available and has correct dimensions
            if (canvasRef.current && canvas.width > 0 && canvas.height > 0) {
              // Calculate aspect ratio to maintain signature proportions
              const imgAspectRatio = img.width / img.height;
              const canvasAspectRatio = canvas.width / canvas.height;

              let drawWidth, drawHeight, drawX, drawY;

              if (imgAspectRatio > canvasAspectRatio) {
                // Image is wider than canvas aspect ratio - fit to width
                drawWidth = canvas.width * 0.8; // Use 80% of canvas width
                drawHeight = drawWidth / imgAspectRatio;
                drawX = (canvas.width - drawWidth) / 2;
                drawY = (canvas.height - drawHeight) / 2;
              } else {
                // Image is taller than canvas aspect ratio - fit to height
                drawHeight = canvas.height * 0.8; // Use 80% of canvas height
                drawWidth = drawHeight * imgAspectRatio;
                drawX = (canvas.width - drawWidth) / 2;
                drawY = (canvas.height - drawHeight) / 2;
              }

              ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
            }
            setSignatureLoaded(true);
          };
          img.onerror = () => {
            setSignatureLoaded(true);
          };
          img.src = parsed.signature;
        } else {
          setSignatureLoaded(true);
        }
      } catch (e) {
        // Handle case where field.value is just a data URL
        if (field.value && field.value.startsWith("data:image/")) {
          const img = new Image();
          img.onload = () => {
            if (canvasRef.current && canvas.width > 0 && canvas.height > 0) {
              // Calculate aspect ratio to maintain signature proportions
              const imgAspectRatio = img.width / img.height;
              const canvasAspectRatio = canvas.width / canvas.height;

              let drawWidth, drawHeight, drawX, drawY;

              if (imgAspectRatio > canvasAspectRatio) {
                // Image is wider than canvas aspect ratio - fit to width
                drawWidth = canvas.width * 0.8; // Use 80% of canvas width
                drawHeight = drawWidth / imgAspectRatio;
                drawX = (canvas.width - drawWidth) / 2;
                drawY = (canvas.height - drawHeight) / 2;
              } else {
                // Image is taller than canvas aspect ratio - fit to height
                drawHeight = canvas.height * 0.8; // Use 80% of canvas height
                drawWidth = drawHeight * imgAspectRatio;
                drawX = (canvas.width - drawWidth) / 2;
                drawY = (canvas.height - drawHeight) / 2;
              }

              ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
            }
            setSignatureLoaded(true);
          };
          img.onerror = () => {
            setSignatureLoaded(true);
          };
          img.src = field.value;
        } else {
          setSignatureLoaded(true);
        }
      }
    } else {
      setSignatureLoaded(true);
    }
  }, [field, setSignatureLoaded]);

  // Separate useEffect to handle signature loading when canvas becomes available
  useEffect(() => {
    if (show && canvasRef.current && field?.value) {
      // Small delay to ensure canvas is fully rendered
      setTimeout(() => {
        loadSignatureOnCanvas();
      }, 100);
    }
  }, [show, field, loadSignatureOnCanvas]);

  // Reset to View 1 whenever modal opens
  useEffect(() => {
    if (show) {
      setShowDrawPad(false);
      setSignatureLoaded(false); // Reset loading state when modal opens
      setFullName(""); // Reset full name input
      setSaveConsent(false); // Reset consent checkbox
      setValidationError(""); // Reset validation error
      setUseSavedSignature(false); // Reset saved signature selection
    }
  }, [show]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setUseSavedSignature(false);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setUseSavedSignature(false);
    setValidationError("");
  };

  const isCanvasEmpty = () => {
    const canvas = canvasRef.current;
    if (!canvas) return true;

    const ctx = canvas.getContext("2d");
    const pixelData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

    // Check if any pixel has non-zero alpha value (i.e., has been drawn on)
    for (let i = 3; i < pixelData.length; i += 4) {
      if (pixelData[i] !== 0) {
        return false;
      }
    }
    return true;
  };

  const handleDrawCustomSignature = () => {
    setShowDrawPad(true);
    clearCanvas();
    setValidationError("");
    setFullName(""); // Reset full name when switching to draw custom signature
    setSaveConsent(false); // Reset consent when switching to draw custom signature
  };

  const handleShowSavedSignature = () => {
    setShowDrawPad(false);
    setValidationError("");
    // Use setTimeout to ensure canvas is ready after state change
    setTimeout(() => {
      if (field && field.value) {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        try {
          const parsed =
            typeof field.value === "string"
              ? JSON.parse(field.value)
              : field.value;

          if (parsed.signature && parsed.signature.startsWith("data:image/")) {
            const img = new Image();
            img.onload = () => {
              ctx.drawImage(img, 0, 0);
            };
            img.src = parsed.signature;
          }
        } catch (e) {
          if (field.value && field.value.startsWith("data:image/")) {
            const img = new Image();
            img.onload = () => {
              ctx.drawImage(img, 0, 0);
            };
            img.src = field.value;
          }
        }
      }
    }, 100); // Small delay to ensure canvas is ready
  };

  const handleSign = () => {
    // Clear previous validation error
    setValidationError("");

    // Check if user is trying to sign with a custom signature (View 2)
    if (showDrawPad) {
      // Validate that canvas has been drawn on
      if (isCanvasEmpty()) {
        setValidationError("Please provide your signature before signing.");
        return;
      }

      // Validate full name
      if (!fullName.trim()) {
        setValidationError("Please enter your full name.");
        return;
      }

      // Validate consent checkbox
      if (!saveConsent) {
        setValidationError(
          "Please give consent to save your signature for future instances.",
        );
        return;
      }
    } else {
      // Check if user is trying to sign without using saved signature (View 1)
      if (!useSavedSignature) {
        setValidationError(
          'Please select "Sign using my saved signature above" or draw a custom signature.',
        );
        return;
      }
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const signatureDataUrl = canvas.toDataURL();

    if (typeof updateField === "function") {
      // Get existing signature data to preserve other properties
      let existingData = {};
      if (field && field.value) {
        try {
          existingData =
            typeof field.value === "string" && field.value.startsWith("{")
              ? JSON.parse(field.value)
              : field.value;
        } catch (e) {
          existingData = {};
        }
      }

      const updatedField = {
        ...field,
        value: JSON.stringify({
          ...existingData, // Preserve existing required, fontSize, fontStyle, etc.
          signerType: signerType || existingData.signerType || "", // Preserve the signerType set by admin
          signature: signatureDataUrl, // Update with new signature
          fullName: showDrawPad ? fullName : existingData.fullName, // Include full name for custom signatures
        }),
      };
      updateField(updatedField);
    }

    // Close both modals after signing in both scenarios
    if (typeof onHide === "function") {
      onHide();
    }
    if (typeof onCloseParentModal === "function") {
      onCloseParentModal();
    }
  };

  return (
    <Modal
      show={show}
      centered
      onHide={onHide}
      className="common-popup esignature-modal"
    >
      <Modal.Header className="settings-modal-header">
        <div className="form-modal">
          <Modal.Title>SIGNATURE</Modal.Title>
        </div>
        <CloseButton onClick={onHide} className="close-cta-wrapper" />
      </Modal.Header>
      <Modal.Body>
        <div className="esignature-form">
          {!showDrawPad ? (
            // View 1: Show saved signature (read-only)
            <>
              <div className="signature-canvas-wrapper">
                {(() => {
                  let hasSignature = false;
                  let signerType = "";
                  if (field && field.value) {
                    try {
                      const parsed =
                        typeof field.value === "string"
                          ? JSON.parse(field.value)
                          : field.value;
                      signerType = parsed.signerType || "";
                      hasSignature =
                        parsed.signature &&
                        parsed.signature.startsWith("data:image/");
                    } catch (e) {
                      hasSignature =
                        field.value && field.value.startsWith("data:image/");
                    }
                  }
                  // Only show saved signature canvas if signerType is "Mine"
                  return hasSignature && signerType === "Mine" ? (
                    <canvas
                      key={`signature-canvas-${field?.value || "empty"}-${signatureLoaded}`}
                      ref={canvasRef}
                      id="signatureCanvas"
                      width={400}
                      height={200}
                      className="signature-canvas"
                      style={{
                        border: "1px solid #ccc",
                        backgroundColor: "#fff",
                      }}
                    />
                  ) : (
                    <div className="no-signature-message">
                      There is no saved signature here, draw a custom signature
                    </div>
                  );
                })()}
              </div>

              <div className="signature-options">
                {(() => {
                  let hasSignature = false;
                  let signerType = "";
                  if (field && field.value) {
                    try {
                      const parsed =
                        typeof field.value === "string"
                          ? JSON.parse(field.value)
                          : field.value;
                      signerType = parsed.signerType || "";
                      hasSignature =
                        parsed.signature &&
                        parsed.signature.startsWith("data:image/");
                    } catch (e) {
                      hasSignature =
                        field.value && field.value.startsWith("data:image/");
                    }
                  }
                  // Only show saved signature option if signerType is "Mine"
                  return (
                    hasSignature &&
                    signerType === "Mine" && (
                      <>
                        <div className="form-group checkbox-group">
                          <input
                            type="checkbox"
                            id="useSavedSignature"
                            name="useSavedSignature"
                            checked={useSavedSignature}
                            onChange={(e) =>
                              setUseSavedSignature(e.target.checked)
                            }
                          />
                          <label
                            className="checkbox-label"
                            htmlFor="useSavedSignature"
                          >
                            Sign using my saved signature above
                          </label>
                        </div>
                        {validationError && (
                          <div className="validation-error">
                            {validationError}
                          </div>
                        )}
                      </>
                    )
                  );
                })()}

                <div className="signature-buttons">
                  <CommonButton
                    type="button"
                    btnText="DRAW A CUSTOM SIGNATURE"
                    onClick={handleDrawCustomSignature}
                    variant="primary"
                  />

                  {(() => {
                    let hasSignature = false;
                    if (field && field.value) {
                      try {
                        const parsed =
                          typeof field.value === "string"
                            ? JSON.parse(field.value)
                            : field.value;
                        hasSignature =
                          parsed.signature &&
                          parsed.signature.startsWith("data:image/");
                      } catch (e) {
                        hasSignature =
                          field.value && field.value.startsWith("data:image/");
                      }
                    }
                    return (
                      hasSignature && (
                        <CommonButton
                          onClick={handleSign}
                          variant="primary"
                          btnText="SIGN"
                        />
                      )
                    );
                  })()}
                </div>
              </div>
            </>
          ) : (
            // View 2: Draw custom signature with full name and consent
            <>
              <div className="signature-canvas-wrapper">
                <div className="canvas-label">Please draw your sign</div>
                <canvas
                  ref={canvasRef}
                  id="signatureCanvas"
                  width={300}
                  height={100}
                  className="signature-canvas"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  style={{ border: "1px solid #ccc", backgroundColor: "#fff" }}
                />
                <CommonButton
                  type="button"
                  btnText="RESET"
                  onClick={clearCanvas}
                  variant="secondary"
                />
              </div>

              <div className="signature-options">
                <div className="form-group">
                  <label htmlFor="fullName">Full Name</label>
                  <input
                    type="text"
                    id="fullName"
                    className="form-control"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group checkbox-group">
                  <input
                    type="checkbox"
                    id="saveConsent"
                    name="saveConsent"
                    checked={saveConsent}
                    onChange={(e) => setSaveConsent(e.target.checked)}
                  />
                  <label className="checkbox-label" htmlFor="saveConsent">
                    I give consent to Guhroo to save my signature to use for
                    future instances.
                  </label>
                </div>
                {validationError && (
                  <div className="validation-error">{validationError}</div>
                )}
                <div className="signature-buttons">
                  <CommonButton
                    type="button"
                    btnText="SHOW SAVED SIGNATURE"
                    onClick={handleShowSavedSignature}
                    variant="secondary"
                  />

                  <CommonButton
                    onClick={handleSign}
                    variant="primary"
                    btnText="SIGN"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ESignatureModal;
